import { FC, useRef } from 'react';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';

import Image from 'app/components/Image';
import { TippyDefault } from 'app/components/Tippy';
import Typography from 'app/components/Typography';

import { formatDate } from 'app/helpers';
import { useBoolean } from 'app/hooks';
import { isEmpty, isNil } from 'lodash';

import { changeStatusAlert, getTotalUnreadAlert } from './core/request';
import { Alert } from './core/type';
import { useAlertStore } from './store';
import { KTCard } from 'theme/helpers';

const Content = styled.div<{ $isRead: boolean }>`
  flex: 1 1 0;

  .card:hover {
    cursor: ${({ $isRead }) => ($isRead ? 'default' : 'pointer')};
  }
  .stockrun-link {
    color: var(--primary);
    cursor: ${({ $isRead }) => ($isRead ? 'default' : 'pointer')};
    z-index: 9;
    font-weight: 600;
    text-decoration: underline;
    :hover {
      text-decoration: underline !important;
    }
  }
`;

const Point = styled.div`
  width: 10px;
  height: 10px;
  background: var(--danger);
  border-radius: 50%;
  margin-right: 8px;
`;

interface AlertItemProps {
  alert: Alert;
  isShowImg?: boolean;
}

export const AlertItem: FC<AlertItemProps> = ({ alert, isShowImg = false }) => {
  const contentRef = useRef<any>();
  const isTooltipVisible = useBoolean(false);

  const queryClient = useQueryClient();

  const onChangeStatusAlert = (id: number, isRead: boolean) => async () => {
    if (isRead) return;

    const response = await changeStatusAlert(id);

    if (response) {
      useAlertStore.getState().onChangeReadAlert(id);

      queryClient
        .getQueryCache()
        .findAll(['alerts'])
        .forEach((query) => {
          const data: any = query.state.data;

          if (data && data.pages) {
            queryClient.setQueryData(query.queryKey, {
              ...data,
              pages: data.pages.map((page: any) => ({
                ...page,
                items: page.items?.map((item: Alert) =>
                  item.id === id ? { ...item, is_read: true } : item,
                ),
              })),
            });
          }
        });

      getTotalUnreadAlert();
    }
  };

  return (
    <Content
      $isRead={alert.is_read}
      onClick={onChangeStatusAlert(alert.id, alert.is_read)}
      ref={contentRef}
      onMouseEnter={() => {
        isTooltipVisible.setValue(true);
      }}
      onMouseLeave={() => {
        isTooltipVisible.setValue(false);
      }}
    >
      <TippyDefault
        arrow={false}
        placement="top"
        content={!alert.is_read ? `Mark as read` : ''}
        delay={[300, 0]}
        visible={isTooltipVisible.value && !alert.is_read}
      >
        <KTCard
          contentStyle={{
            padding: '24px 28px 24px 10px',
            backgroundColor: alert.is_read ? '#FFFFFF' : 'hsl(345.34deg 86.27% 60% / 7%)',
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex flex-column">
              <div className="d-flex align-items-start">
                <div className="d-flex align-items-center">
                  {!alert.is_read && (
                    <div className="mr-2" style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Point
                        className="point"
                        style={{
                          marginRight:
                            isEmpty(alert.content.image) && !isNil(alert.content.image) ? 56 : '',
                        }}
                      ></Point>
                    </div>
                  )}

                  {isShowImg && (
                    <Image
                      src={alert.content.image!}
                      alt="Product"
                      width={56}
                      height={56}
                      radius={8}
                      style={{
                        marginRight: !alert.is_read ? 18 : 0,
                        marginLeft: alert.is_read ? 18 : 0,
                      }}
                    />
                  )}

                  <span
                    style={{
                      marginRight:
                        isEmpty(alert.content.image) && !isNil(alert.content.image) ? 50 : '',
                    }}
                  />

                  <div
                    dangerouslySetInnerHTML={{
                      __html: `${alert.content.description}`,
                    }}
                    style={{
                      paddingLeft: alert.is_read ? 18 : 0,
                      paddingRight: 16,
                      fontSize: 16,
                      lineHeight: '26px',
                    }}
                  />
                </div>
              </div>
            </div>
            <Typography variant="body-4" style={{ minWidth: 140 }}>
              {formatDate(alert.createdAt, 'dd MMM yyyy')} at{' '}
              {formatDate(alert.createdAt, 'H:mm:ss')}
            </Typography>
          </div>
        </KTCard>
      </TippyDefault>
    </Content>
  );
};
